/**
 * media panel 入口按钮可见性决策（PRD 真值表 5 × 2 × 2）。
 *
 * 抽出来的核心动机：
 *   - ChatHeader 原本把 `activeClaw?.id && (status === ACTIVE || status === EXPIRED)` 判断
 *     和 `folderLoading` 切换 spinner 的逻辑揉在 JSX 里，难以单测；
 *   - 把"该不该渲染按钮 / 渲染时是否 disabled"的决策抽成纯函数，渲染层只做 mapping。
 *
 * 三态语义：
 *   - 'hidden'  → 不渲染 <button>
 *   - 'loading' → 渲染按钮，disabled + aria-busy + spinner，onClick 即便绑定也不响应
 *   - 'visible' → 渲染按钮，文件夹图标，正常响应点击
 */

export type ClawStatusForVisibility =
  | 'CLAW_STATUS_UNSPECIFIED'
  | 'CLAW_STATUS_CREATING'
  | 'CLAW_STATUS_PENDING'
  | 'CLAW_STATUS_ACTIVE'
  | 'CLAW_STATUS_EXPIRED';

export interface MediaPanelButtonInputs {
  /** 当前选中 claw 的 id；null/undefined/'' 都视作未选中 → 隐藏。 */
  activeClawId: string | null | undefined;
  /** claw 服务端状态；只有 ACTIVE / EXPIRED 才暴露 media 入口。 */
  clawStatus: ClawStatusForVisibility | null | undefined;
  /** 素材列表正在加载（包括点击触发后的拉取窗口）。 */
  folderLoading: boolean;
}

export type MediaPanelButtonVisibility = 'hidden' | 'loading' | 'visible';

const VISIBLE_STATUSES = new Set<ClawStatusForVisibility>(['CLAW_STATUS_ACTIVE', 'CLAW_STATUS_EXPIRED']);

export const computeMediaPanelButtonVisibility = ({
  activeClawId,
  clawStatus,
  folderLoading,
}: MediaPanelButtonInputs): MediaPanelButtonVisibility => {
  if (!activeClawId) return 'hidden';
  if (!clawStatus || !VISIBLE_STATUSES.has(clawStatus)) return 'hidden';
  return folderLoading ? 'loading' : 'visible';
};
